import React from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ROUTE_DATE_RANGE_PRESET_OPTIONS,
  DateRangePresetId,
} from "app/utils/dateRangePresets";
import type { Dayjs } from "dayjs";

export type RouteStateFilter =
  | "all"
  | "active"
  | "draft"
  | "assigned"
  | "loaded"
  | "in_progress"
  | "done"
  | "cancelled";

type MobileRoutesFilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  stateFilter: RouteStateFilter;
  onStateFilterChange: (value: RouteStateFilter) => void;
  dateRangePreset: DateRangePresetId | "";
  onDateRangePresetChange: (preset: DateRangePresetId) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (value: Dayjs | null) => void;
  onToDateChange: (value: Dayjs | null) => void;
};

export function MobileRoutesFilterDrawer({
  open,
  onClose,
  stateFilter,
  onStateFilterChange,
  dateRangePreset,
  onDateRangePresetChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}: MobileRoutesFilterDrawerProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "88vh",
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 999,
            bgcolor: "#d1d5db",
            mx: "auto",
            mb: 2,
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Filters
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={stateFilter}
            onChange={(event) =>
              onStateFilterChange(event.target.value as RouteStateFilter)
            }
          >
            <MenuItem value="active">Active routes</MenuItem>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="loaded">Loaded</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="done">Done</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Quick range</InputLabel>
          <Select
            label="Quick range"
            value={dateRangePreset}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return "Custom";
              }
              return (
                ROUTE_DATE_RANGE_PRESET_OPTIONS.find((option) => option.id === selected)
                  ?.label ?? "Custom"
              );
            }}
            onChange={(event) =>
              onDateRangePresetChange(event.target.value as DateRangePresetId)
            }
          >
            {ROUTE_DATE_RANGE_PRESET_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={onFromDateChange}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <DatePicker
            label="To"
            value={toDate}
            onChange={onToDateChange}
            minDate={fromDate ?? undefined}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: "#5fa67a",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#4d9164" },
          }}
        >
          Apply filters
        </Button>
      </Box>
    </Drawer>
  );
}
